/**
 * Works out *how many* locations an instruction targets.
 *
 * Naming a single location is the common case, but "all my active locations" is
 * a genuinely different targeting mode: it selects a set the user never
 * enumerated, so getting it wrong edits listings they never mentioned. Scope
 * detection is therefore explicit and narrow — an unrecognised phrase falls back
 * to single-location name matching rather than being treated as "everything".
 *
 * Status is not available from the bulk-edit export (the server exports every
 * location for the account and the CSV carries no status column), so
 * status-filtered scopes are resolved against the page's own location list.
 */

import type { LocationStatus } from '../../types/LocationStatus';
import { normalizeName } from './locationResolver';

export type LocationScope =
  | { kind: 'ALL' }
  | { kind: 'STATUS'; status: LocationStatus; word: string }
  | { kind: 'NAMED' };

/**
 * Status words accepted in a scope phrase, mapped to the derived UI status.
 * Longer phrases are matched first so "not active" cannot be read as "active".
 */
const STATUS_WORDS: { word: string; status: LocationStatus }[] = [
  { word: 'deactivated', status: 'deactivated' },
  { word: 'inactive', status: 'deactivated' },
  { word: 'pending', status: 'pending' },
  { word: 'draft', status: 'draft' },
  { word: 'active', status: 'active' },
  { word: 'live', status: 'active' },
];

/** Determiners that can introduce a whole-account scope. */
const DETERMINERS = ['all of my', 'all of the', 'all my', 'all the', 'all', 'every', 'each'];

const NOUNS = ['locations', 'location', 'yards', 'yard', 'sites', 'site'];

interface ScopeDetection {
  scope: LocationScope;
  /** The matched phrase, so callers can exclude it from name matching. */
  phrase: string;
}

/**
 * Builds the regular expression for a scope phrase: a determiner, an optional
 * status word, then a location noun. Matching the noun is required — "all" on
 * its own is far more likely to be part of a sentence than a scope.
 */
const buildScopePattern = (): RegExp => {
  const determiners = DETERMINERS.join('|');
  const statuses = STATUS_WORDS.map(({ word }) => word).join('|');
  const nouns = NOUNS.join('|');
  return new RegExp(`\\b(${determiners})\\s+(?:(${statuses})\\s+)?(${nouns})\\b`);
};

const SCOPE_PATTERN = buildScopePattern();

/**
 * Detects a whole-account scope phrase in `message`.
 *
 * Returns `NAMED` when no scope phrase is present, which leaves the existing
 * single-location name resolution in charge.
 */
export const detectScope = (message: string): ScopeDetection => {
  const match = SCOPE_PATTERN.exec(normalizeName(message));

  if (!match) return { scope: { kind: 'NAMED' }, phrase: '' };

  const [phrase, , statusWord] = match;

  if (!statusWord) return { scope: { kind: 'ALL' }, phrase };

  const entry = STATUS_WORDS.find(({ word }) => word === statusWord);
  if (!entry) return { scope: { kind: 'ALL' }, phrase };

  return { scope: { kind: 'STATUS', status: entry.status, word: statusWord }, phrase };
};

/**
 * Removes the scope phrase from a message so a location name can be looked for
 * in what remains.
 *
 * Without this, a location literally named "All" would match the word "all" in
 * "all my locations" and quietly narrow a whole-account edit down to one row.
 */
export const stripScopePhrase = (message: string, phrase: string): string => {
  if (!phrase) return message;
  const normalized = normalizeName(message);
  const index = normalized.indexOf(phrase);
  if (index === -1) return message;
  return `${normalized.slice(0, index)} ${normalized.slice(index + phrase.length)}`;
};

/** Human-readable description of a scope, for confirmation copy. */
export const describeScope = (scope: LocationScope, count: number): string => {
  const plural = `${count} location${count === 1 ? '' : 's'}`;
  if (scope.kind === 'STATUS') return `${plural} that ${count === 1 ? 'is' : 'are'} ${scope.word}`;
  return plural;
};
